// Hacking presentation (review flag ?hackfx=1). Hacks are instant: nothing here is an upload, a progress fill or a delay.
// Input acknowledgement (a short dotted link from the local player) is drawn at the key press; every consequence is
// drawn only from the server's accepted `hack` event (tk = target key, du = seconds) and the snapshot's `inf.res`
// (reserved key -> seconds left), so teammates, late joiners and reconnects see the same real state.
import { HACK_FX } from './fx-flags';
import type { Graphics } from 'pixi.js';
import { CITY_ART, CONFIG, WORLD, HackId } from '../../common/src/data';
import { TILE } from '../../common/src/map';
import type { GameEvent, NpcSnap, Snapshot, VehicleSnap } from '../../common/src/protocol';
import { TargetKind, hacksFor, type RefusalCode } from '../../common/src/hacks';
import { COLOR } from './theme';
import { FxPool } from './fx-pool';
import { hackRange } from './targets';

export const HF = CITY_ART.hackFx;
export const HACK_FX_REVIEW = HACK_FX;

type Pt = { x: number; y: number };
type Link = { x0: number; y0: number; x1: number; y1: number; at: number; hack: HackId; ack: boolean; seed: number };
type Burst = { key: string; kind: TargetKind; x: number; y: number; at: number; hack: HackId; heading?: number; raised?: boolean; seed: number };
type Refusal = { key: string; x: number; y: number; at: number; reason: string };
export interface HackLabel { x: number; y: number; text: string; tone: 'select' | 'info' | 'danger' | 'dim' }
export interface ActiveState { key: string; kind: TargetKind; x: number; y: number; left: number; tag: string; angle?: number; raised?: boolean }
export type MarkState = 'ready' | 'far' | 'busy';
interface Target { key: string; kind: TargetKind; x: number; y: number; npcKind?: string; label: string }

/** Pure: how a scanned target should read. Reserved wins, then range of the longest applicable hack. */
export function targetState(t: Target, px: number, py: number, res: Record<string, number>): { state: MarkState; left: number } {
  const left = res[t.key] ?? 0;
  if (left > 0) return { state: 'busy', left };
  const reach = Math.max(0, ...hacksFor(t.kind, t.npcKind).map(hackRange));
  return { state: Math.hypot(t.x - px, t.y - py) > reach ? 'far' : 'ready', left: 0 };
}

/** Short label for the server's refusal code (`PagerMsg.rc`). */
export const REFUSAL_LABEL: Record<RefusalCode, string> = {
  down: 'DOWN', unknown: 'INVALID', cooldown: 'COOLDOWN', ram: 'LOW RAM', invalid: 'INVALID', cant: 'CAN\'T HACK THAT',
  ineligible: 'NOT ELIGIBLE', range: 'OUT OF RANGE', busy: 'BUSY', blocked: 'BLOCKED',
};
/** Base seconds the server keeps the effect (perks may lengthen it); 0 = instant damage. Mirrors Room.applyHack, data only. */
export function hackDuration(h: HackId, kind: TargetKind): number {
  const C = CONFIG.hacks;
  switch (kind) {
    case 'sig': return C.remote_override.signalDuration;
    case 'bar': return C.remote_override.barrierDuration;
    case 'cam': return h === 'short_circuit' ? C.sensor_jam.duration * 1.5 : C.sensor_jam.duration;
    case 'fix': return C.short_circuit.hazardDuration;
    case 'vent': return CONFIG.vent.duration;
    case 'veh': return C.remote_override.vehicleDuration;
    case 'npc': return h === 'sensor_jam' ? C.sensor_jam.duration : 0;
  }
}

const TAGS: Record<TargetKind, string> = { sig: 'ALL-RED', bar: 'BARRIER', cam: 'JAMMED', fix: 'OVERLOAD', vent: 'VENT OPEN', veh: 'REMOTE', npc: 'JAMMED' };

/** Pure: every server-reserved target near the camera, with real seconds left. Bounded by the reserved set, never the world. */
export function activeStates(s: Snapshot, intersections: { cx: number; cy: number }[], vehicles: VehicleSnap[], npcs: NpcSnap[], cx: number, cy: number, range: number): ActiveState[] {
  const out: ActiveState[] = [];
  for (const [key, left] of Object.entries(s.inf.res)) {
    if (!(left > 0)) continue;
    const [k, n] = key.split(':'), i = Number(n);
    let st: ActiveState | null = null;
    if (k === 'sig') { const it = intersections[i]; if (it) st = { key, kind: 'sig', x: it.cx, y: it.cy, left, tag: TAGS.sig }; }
    else if (k === 'bar') { const b = WORLD.barriers[i]; if (b) { const raised = !!s.inf.bar[i]; st = { key, kind: 'bar', x: (b.x + b.w / 2) * TILE, y: (b.y + b.h / 2) * TILE, left, raised, tag: raised ? 'RAISED' : 'LOWERED' }; } }
    else if (k === 'cam') { const c = WORLD.cameras[i]; if (c && s.inf.cam[i]?.[1]) st = { key, kind: 'cam', x: c.x * TILE, y: c.y * TILE, left, tag: TAGS.cam }; }
    else if (k === 'fix') { const f = WORLD.fixtures[i]; if (f && s.inf.fix[i] > 0) st = { key, kind: 'fix', x: f.x * TILE, y: f.y * TILE, left: s.inf.fix[i], tag: TAGS.fix }; }
    else if (k === 'vent') { const v = WORLD.vents[i]; if (v && s.inf.vent[i] > 0) st = { key, kind: 'vent', x: v.x * TILE, y: v.y * TILE, left: s.inf.vent[i], tag: TAGS.vent }; }
    else if (k === 'veh') { const v = vehicles.find(e => e.id === i); if (v && v.f & 2) st = { key, kind: 'veh', x: v.x, y: v.y, left, angle: v.a, tag: TAGS.veh }; }
    else if (k === 'npc') { const e = npcs.find(q => q.id === i); if (e && e.j) st = { key, kind: 'npc', x: e.x, y: e.y, left, tag: TAGS.npc }; }
    if (st && Math.abs(st.x - cx) < range && Math.abs(st.y - cy) < range) out.push(st);
    if (out.length >= HF.labels) break;
  }
  return out;
}

const r = Math.round;
const hash = (a: number, b = 0) => { const v = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return v - Math.floor(v); };

function brackets(g: Graphics, x: number, y: number, h: number, len: number, color: string, alpha: number, width = 1) {
  x = r(x); y = r(y);
  for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) g.moveTo(x + sx * h, y + sy * (h - len)).lineTo(x + sx * h, y + sy * h).lineTo(x + sx * (h - len), y + sy * h);
  g.stroke({ color, width, alpha });
}
function arrow(g: Graphics, x: number, y: number, a: number, size: number) {
  const c = Math.cos(a), s = Math.sin(a);
  g.moveTo(r(x - c * size - s * size), r(y - s * size + c * size)).lineTo(r(x), r(y)).lineTo(r(x - c * size + s * size), r(y - s * size - c * size));
}
function dashedCircle(g: Graphics, x: number, y: number, rad: number, n: number, phase: number, color: string, alpha: number, width = 1) {
  for (let i = 0; i < n; i++) {
    const a0 = (i / n + phase) * Math.PI * 2, a1 = a0 + Math.PI / n;
    g.moveTo(r(x + Math.cos(a0) * rad), r(y + Math.sin(a0) * rad)).lineTo(r(x + Math.cos((a0 + a1) / 2) * rad), r(y + Math.sin((a0 + a1) / 2) * rad)).lineTo(r(x + Math.cos(a1) * rad), r(y + Math.sin(a1) * rad));
  }
  g.stroke({ color, width, alpha });
}
/** A ring with three gaps: the sensor reads as interrupted */
function brokenRing(g: Graphics, x: number, y: number, rad: number, rot: number, color: string, alpha: number) {
  for (let i = 0; i < 3; i++) {
    const a0 = rot + i * Math.PI * 2 / 3 + 0.35, a1 = a0 + Math.PI * 2 / 3 - 0.7;
    g.moveTo(r(x + Math.cos(a0) * rad), r(y + Math.sin(a0) * rad));
    for (let k = 1; k <= 4; k++) { const a = a0 + (a1 - a0) * k / 4; g.lineTo(r(x + Math.cos(a) * rad), r(y + Math.sin(a) * rad)); }
  }
  g.stroke({ color, width: 1, alpha });
}
function segment(g: Graphics, a: Pt, b: Pt, mode: 'dot' | 'solid' | 'jag' | 'gap', seed: number) {
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1, ux = dx / len, uy = dy / len, n = Math.max(1, Math.floor(len / 4));
  if (mode === 'solid') { g.moveTo(r(a.x), r(a.y)).lineTo(r(b.x), r(b.y)); return; }
  if (mode === 'jag') { g.moveTo(r(a.x), r(a.y)); for (let i = 1; i < n; i++) { const o = (hash(i, seed) - 0.5) * 7; g.lineTo(r(a.x + ux * i * 4 - uy * o), r(a.y + uy * i * 4 + ux * o)); } g.lineTo(r(b.x), r(b.y)); return; }
  for (let i = 0; i < n; i += 2) { // dotted; 'gap' leaves a hole in the middle
    if (mode === 'gap' && Math.abs(i - n / 2) < 2) continue;
    g.moveTo(r(a.x + ux * i * 4), r(a.y + uy * i * 4)).lineTo(r(a.x + ux * (i * 4 + 2)), r(a.y + uy * (i * 4 + 2)));
  }
}

export class HackFx {
  readonly links = new FxPool<Link>(HF.links);
  readonly bursts = new FxPool<Burst>(HF.bursts);
  readonly refusals = new FxPool<Refusal>(HF.refusals);
  /** the last local request, so a server refusal (which names no target) can be drawn where it was aimed */
  attempt: { key: string; x: number; y: number; at: number } | null = null;
  labels: HackLabel[] = [];
  private serial = 0;

  /** Input acknowledgement only. Draws a short dotted link; the world has not changed yet. */
  ack(from: Pt, to: Pt, hack: HackId, key: string, t: number, predictedOk: boolean) {
    this.attempt = { key, x: to.x, y: to.y, at: t };
    if (predictedOk) this.links.put({ x0: from.x, y0: from.y, x1: to.x, y1: to.y, at: t, hack, ack: true, seed: this.serial++ });
  }
  /** An accepted hack, from the authoritative event: the same drawing for the hacker and everyone else. */
  confirm(e: GameEvent, hacker: Pt | undefined, heading: number | undefined, raised: boolean | undefined, t: number) {
    if (!e.tk || !e.w) return;
    const hack = e.w as HackId, kind = e.tk.split(':')[0] as TargetKind, seed = this.serial++;
    if (hacker) this.links.put({ x0: hacker.x, y0: hacker.y, x1: e.x, y1: e.y, at: t, hack, ack: false, seed });
    this.bursts.put({ key: e.tk, kind, x: e.x, y: e.y, at: t, hack, heading, raised, seed });
    if (this.attempt?.key === e.tk) this.attempt = null;
  }
  /** A server refusal (its 
c code) for the last local request. Returns the label shown, or null (no code, stale, or a burst of repeats). */
  refuse(code: RefusalCode | undefined, t: number): string | null {
    const reason = code && REFUSAL_LABEL[code], a = this.attempt;
    if (!reason || !a || t - a.at > 1.5) return null;
    if (this.refusals.items.some(f => f.key === a.key && t - f.at < HF.refuseGapSec)) return null;
    this.refusals.put({ key: a.key, x: a.x, y: a.y, at: t, reason });
    return reason;
  }

  draw(g: Graphics, ground: Graphics, t: number, scale: number, c: {
    s: Snapshot; targets: Target[]; highlight: string | null; scan: boolean; me: Pt;
    vehicles: VehicleSnap[]; npcs: NpcSnap[]; intersections: { cx: number; cy: number }[]; cam: Pt;
  }) {
    this.labels = [];
    const label = (x: number, y: number, text: string, tone: HackLabel['tone']) => { this.labels.push({ x: r(x), y: r(y), text, tone }); };
    const A = (a: number) => Math.max(0, Math.min(1, a * scale));
    const step = Math.floor(t * 6) % 3; // integer pulse: 0, 1, 2

    // persistent state: every reserved target, for the server's real seconds left
    const states = activeStates(c.s, c.intersections, c.vehicles, c.npcs, c.cam.x, c.cam.y, 400);
    for (const st of states) {
      const col = COLOR.interactive, x = r(st.x), y = r(st.y), flick = hash(Math.floor(t * 12), x) > 0.9 ? 0.5 : 1;
      if (st.kind === 'sig') {
        for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2; arrow(ground, x + Math.cos(a) * (16 - step), y + Math.sin(a) * (16 - step), a + Math.PI, 3); }
        ground.stroke({ color: col, width: 1, alpha: A(0.9) }); brackets(ground, x, y, 13, 4, col, A(0.8));
      } else if (st.kind === 'bar') {
        const b = WORLD.barriers.find((_, i) => `bar:${i}` === st.key)!, bx = b.x * TILE - 2, by = b.y * TILE - 2, bw = b.w * TILE + 4, bh = b.h * TILE + 4;
        brackets(ground, bx + bw / 2, by + bh / 2, Math.max(bw, bh) / 2, 4, col, A(0.9), 2);
        const along = b.w >= b.h ? 0 : Math.PI / 2, out = st.raised ? -1 : 1; // raised: chevrons close in; lowered: they open out
        for (const side of [-1, 1]) arrow(ground, st.x + Math.cos(along) * side * (Math.max(bw, bh) / 2 + 6 - out * step), st.y + Math.sin(along) * side * (Math.max(bw, bh) / 2 + 6 - out * step), along + (side > 0 ? 0 : Math.PI) + (st.raised ? Math.PI : 0), 3);
        ground.stroke({ color: col, width: 1, alpha: A(0.9) });
      } else if (st.kind === 'fix') {
        const hr = CONFIG.hacks.short_circuit.hazardRadius; ground.circle(x, y, hr).fill({ color: COLOR.danger, alpha: A(0.14) }); dashedCircle(ground, x, y, hr, 14, (step % 3) / 14, COLOR.danger, A(flick), 2); dashedCircle(ground, x, y, hr - 3, 14, (step % 3) / 14 + 0.5 / 14, COLOR.text, A(0.5 * flick));
      } else if (st.kind === 'vent') {
        dashedCircle(ground, x, y, CONFIG.vent.radius, 20, 0, col, A(0.6));
      } else if (st.kind === 'cam') {
        brokenRing(g, x, y, 9, 0, COLOR.text, A(0.9)); g.moveTo(x - 6, y + 6).lineTo(x + 6, y - 6).stroke({ color: COLOR.text, width: 1, alpha: A(0.9) });
      } else if (st.kind === 'npc') {
        brokenRing(g, x, y, 11, t * 2, COLOR.text, A(0.9)); g.moveTo(x - 7, y + 7).lineTo(x + 7, y - 7).stroke({ color: COLOR.text, width: 1, alpha: A(0.9) });
      } else if (st.kind === 'veh') {
        brackets(g, x, y, 15, 5, col, A(0.95), 1);
        const a = st.angle ?? 0; arrow(g, x + Math.cos(a) * (20 + step), y + Math.sin(a) * (20 + step), a, 3); g.stroke({ color: col, width: 1, alpha: A(0.95) });
      }
      label(st.x, st.y + (st.kind === 'veh' ? 22 : st.kind === 'fix' ? CONFIG.hacks.short_circuit.hazardRadius + 6 : 14), `${st.tag} ${Math.ceil(st.left)}s`, st.kind === 'fix' ? 'danger' : 'info');
    }

    // scan marks and the locked target, anchored to the interpolated vehicle/drone positions
    if (c.scan) for (const tg of c.targets) {
      if (Math.hypot(tg.x - c.cam.x, tg.y - c.cam.y) > HF.scanRange + 120) continue;
      let x = tg.x, y = tg.y;
      if (tg.kind === 'veh') { const v = c.vehicles.find(e => `veh:${e.id}` === tg.key); if (v) { x = v.x; y = v.y; } }
      else if (tg.kind === 'npc') { const n = c.npcs.find(e => `npc:${e.id}` === tg.key); if (n) { x = n.x; y = n.y; } }
      x = r(x); y = r(y);
      const { state, left } = targetState({ ...tg, x, y }, c.me.x, c.me.y, c.s.inf.res), sel = tg.key === c.highlight, hasState = states.some(s => s.key === tg.key);
      if (state === 'ready') { g.moveTo(x, y - 4).lineTo(x + 4, y).lineTo(x, y + 4).lineTo(x - 4, y).closePath().stroke({ color: COLOR.interactive, width: 1, alpha: A(0.95) }); }
      else if (state === 'far') { for (const [sx, sy] of [[-3, -3], [3, -3], [3, 3], [-3, 3]]) g.rect(x + sx, y + sy, 1, 1); g.fill({ color: COLOR.interactive, alpha: A(0.6) }); }
      else { g.rect(x - 4, y - 4, 8, 8).moveTo(x - 4, y - 4).lineTo(x + 4, y + 4).moveTo(x + 4, y - 4).lineTo(x - 4, y + 4).stroke({ color: COLOR.text, width: 1, alpha: A(0.9) }); }
      if (sel) {
        brackets(g, x, y, 11 + step, 4, COLOR.interactive, A(1), 2); brackets(g, x, y, 11 + step, 4, COLOR.ground, A(0.5), 1);
      }
      // one label per target: a hacked object already carries its state tag and seconds; the others get their name (selected) or a short tag
      if (hasState) continue;
      if (sel) label(x, y - (tg.kind === 'veh' ? 24 : 18), tg.label.toUpperCase() + (state === 'busy' ? ` · BUSY ${Math.ceil(left)}s` : state === 'far' ? ' · OUT OF RANGE' : ''), 'select');
      else if (state !== 'ready') label(x, y + 10, state === 'busy' ? `BUSY ${Math.ceil(left)}s` : 'FAR', 'dim');
    }

    // refusals: a small immediate interruption at the target and a concise reason
    for (const f of this.refusals.items) {
      const age = t - f.at; if (age < 0 || age > HF.refuseSec) continue;
      const x = r(f.x), y = r(f.y);
      if (age < 0.25) for (let i = 0; i < 3; i++) { const o = r((hash(i, f.at) - 0.5) * 8), w = 6 + r(hash(i + 3, f.at) * 8); g.moveTo(x - w + o, y - 4 + i * 4).lineTo(x + w + o, y - 4 + i * 4); }
      g.stroke({ color: COLOR.danger, width: 1, alpha: A(Math.max(0, 1 - age / 0.25)) });
      label(f.x, f.y - 32, `X ${f.reason}`, 'danger');
    }

    // links and consequence bursts (brief; recycled from fixed pools)
    for (const l of this.links.items) {
      const dur = l.ack ? HF.ackSec : HF.linkSec, age = t - l.at; if (age < 0 || age > dur) continue;
      const fade = 1 - age / dur, a = { x: l.x0, y: l.y0 }, b = { x: l.x1, y: l.y1 };
      if (l.ack) { segment(g, a, b, 'dot', l.seed); g.stroke({ color: COLOR.interactive, width: 1, alpha: A(0.7 * fade) }); continue; }
      const mode = l.hack === 'short_circuit' ? 'jag' : l.hack === 'sensor_jam' ? 'gap' : 'solid';
      segment(g, a, b, mode, l.seed); g.stroke({ color: COLOR.interactive, width: 2, alpha: A(0.45 * fade) });
      segment(g, a, b, mode, l.seed); g.stroke({ color: COLOR.text, width: 1, alpha: A(fade) });
    }
    for (const b of this.bursts.items) {
      const age = t - b.at; if (age < 0 || age > HF.burstSec) continue;
      const u = age / HF.burstSec, fade = 1 - u, x = r(b.x), y = r(b.y);
      if (b.hack === 'short_circuit') { // sharp breaks radiating from the point of discharge
        for (let i = 0; i < 5; i++) { const a = hash(i, b.seed) * Math.PI * 2, len = 6 + u * 12; let px = x, py = y; g.moveTo(px, py); for (let k = 1; k <= 3; k++) { const j = (hash(i * 3 + k, b.seed) - 0.5) * 1.2; px = r(x + Math.cos(a + j) * len * k / 3); py = r(y + Math.sin(a + j) * len * k / 3); g.lineTo(px, py); } }
        g.stroke({ color: COLOR.text, width: 1, alpha: A(fade) });
      } else if (b.hack === 'sensor_jam') { // two broken sensor rings, widening and fading
        brokenRing(g, x, y, 6 + u * 12, u * 2, COLOR.interactive, A(fade)); brokenRing(g, x, y, 3 + u * 7, -u * 2, COLOR.text, A(fade * 0.8));
      } else { // remote override: a control bracket snaps shut, then arrows say what changed
        brackets(g, x, y, r(16 - Math.min(1, u * 3) * 9), 4, COLOR.interactive, A(fade), 2);
        if (u > 0.15) {
          const dirs = b.kind === 'veh' ? [b.heading ?? 0] : b.kind === 'bar' ? [0, Math.PI] : [0, Math.PI / 2, Math.PI, -Math.PI / 2];
          const inward = b.kind === 'sig' || (b.kind === 'bar' && b.raised);
          for (const a of dirs) { const d = inward ? 20 - u * 8 : 8 + u * 10; arrow(g, x + Math.cos(a) * d, y + Math.sin(a) * d, inward ? a + Math.PI : a, 3); }
          g.stroke({ color: COLOR.interactive, width: 1, alpha: A(fade) });
        }
      }
    }
    // the budget keeps the most useful words: a refusal, then the locked target, then seconds left, then far/busy tags
    const rank = { danger: 0, select: 1, info: 2, dim: 3 };
    this.labels.sort((p, q) => rank[p.tone] - rank[q.tone]); this.labels.length = Math.min(this.labels.length, HF.labels);
  }
}
